import { Box, Card, CardActions, CardContent, Chip, Typography } from '@material-ui/core';
import { capitalize } from 'lodash';
import React from 'react';
import { MomentFormats, useDate } from '../../../../common/utils/date';
import { Approval, ApprovalStatus } from '../../../../state/ducks/common/types';
import ChangeRequestTransitionDialogContainer from '../../../change.request/dialogs/ChangeRequestTransitionDialog.container';
import { FBButton, FBInlineApprovalStatusIdent } from '../../../form.builder';
import { StyledCancelFBButton } from '../../../form.builder/FBButton/StyledCancelFBButton';
import useStyles from './approvalWorkflow.styles';

interface WorkflowApproverProps {
  changeRequestId?: string
  documentRevisionId?: string
  approval: Approval
  isCurrentUser: boolean
  canReview: boolean
  isStageCompleted: boolean
  hasReviewed: boolean
  stageNumber: number
  onApproveClick?: (approvalId: string) => void
  onRejectClick?: (approvalId: string) => void
  onDelete?: (approvalId: string) => void
}

const WorkflowApprover: React.FC<WorkflowApproverProps> = ({
  changeRequestId,
  documentRevisionId,
  approval,
  isCurrentUser,
  canReview,
  isStageCompleted,
  hasReviewed,
  stageNumber,
  onApproveClick,
  onRejectClick,
  onDelete,
}) => {
  const classes = useStyles();
  const { getFormattedDateString } = useDate();

  const getApproverCardClassName = (approval: Approval | undefined, isCurrentUser: boolean): string => {
    const classNames = [classes.approverCard];

    if (approval?.status === ApprovalStatus.Approved) {
      classNames.push(classes.approvedCard);
    } else if (approval?.status === ApprovalStatus.Rejected) {
      classNames.push(classes.rejectedCard);
    } else if (!approval || approval.status === ApprovalStatus.Pending) {
      classNames.push(classes.pendingCard);
    } else if (isCurrentUser) {
      classNames.push(classes.currentUserCard);
    }

    return classNames.join(' ');
  };

  const getApprovalStatusClassName = (status?: ApprovalStatus) => {
    const classNames = [classes.status];
    switch (status) {
      case ApprovalStatus.Approved:
        classNames.push(classes.approvedStatus);
        break;

      case ApprovalStatus.Rejected:
        classNames.push(classes.rejectedStatus);
        break;

      case ApprovalStatus.Pending:
        classNames.push(classes.pendingStatus);
        break;

      case ApprovalStatus.Draft:
        classNames.push(classes.draftStatus);
        break;

      case ApprovalStatus.Abandoned:
        classNames.push(classes.abandonedStatus);
        break;
    }

    return classNames.join(' ');
  };

  const isApprovalDecided
    = approval?.status === ApprovalStatus.Approved
    || approval?.status === ApprovalStatus.Rejected;

  return (
    <Card className={getApproverCardClassName(approval, isCurrentUser)}>
      <CardContent className={classes.cardContent}>
        <div className={classes.approverNameRow}>
          <Typography variant="h5" className={classes.approverName}>
            {capitalize(approval.approver?.user?.name)}

            {!approval.isOptional && (
              <Typography
                component="span"
                className={classes.requiredIndicator}
              >*
              </Typography>
            )}
          </Typography>

          {approval.isOptional && (
            <Chip
              size="small"
              label="Optional"
              className={classes.optionalChip} />
          )}
        </div>

        {approval?.status === ApprovalStatus.Pending && !isStageCompleted
            && <>
              <Typography
                variant="caption"
                className={classes.sectionTitle}
              > Status
              </Typography>

              <Typography className={getApprovalStatusClassName(approval.status)}>
                {FBInlineApprovalStatusIdent[approval.status]}
              </Typography>
            </>
        }

        {
          approval?.status && isApprovalDecided ? (
            <>
              <Typography
                variant="caption"
                className={classes.sectionTitle}
              > Status
              </Typography>

              <Box className={classes.genericFlex}>
                <Typography className={getApprovalStatusClassName(approval.status)}>
                  {FBInlineApprovalStatusIdent[approval.status]}
                </Typography>
                {[ApprovalStatus.Approved].includes(approval.status) && canReview
                  && <StyledCancelFBButton
                    label="common.reject"
                    size="small"
                    style={{ fontSize: '11px' }}
                    className={classes.rejectBtn}
                    onClick={() => onRejectClick?.(approval?.id || '')}
                  /> }
              </Box>

              {approval.reviewedAt && isApprovalDecided && (
                <div>
                  {getFormattedDateString(
                    approval.reviewedAt,
                    MomentFormats.DateTime,
                  )}
                </div>
              )}
            </>
          ) : null}
      </CardContent>

      {canReview && !hasReviewed && changeRequestId && (
        <CardActions>
          <Box flexGrow={1}>
            <ChangeRequestTransitionDialogContainer
              transition="reject"
              changeRequestId={changeRequestId}
              stageNumber={stageNumber}
              label="transition.action.reject"
            />
            <ChangeRequestTransitionDialogContainer
              transition="approve"
              changeRequestId={changeRequestId}
              stageNumber={stageNumber}
              label="transition.action.approve"
            />
          </Box>
        </CardActions>
      )}

      {canReview && !hasReviewed && documentRevisionId && (
        <CardActions>
          <Box flexGrow={1}>
            <StyledCancelFBButton
              label="common.reject"
              size="small"
              style={{ fontSize: '11px' }}
              onClick={() => onRejectClick?.(approval?.id || '')}
            />
            <FBButton
              label="form.builder.request.approve"
              size="small"
              style={{ fontSize: '11px' }}
              onClick={() => onApproveClick?.(approval?.id || '')}
            />
          </Box>
        </CardActions>
      )}
    </Card>
  );
};

export default WorkflowApprover;
